import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _uniqueId from "lodash/uniqueId";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import { Text, View, Page } from '@react-pdf/renderer';
import { getActiveSeriesMeds, getFullyDiscontinuedMeds, makeActiveIdHash } from '@mummssoftware/utils/medications/utils';
import { ACTIVE, DISCONTINUED, PENDING, AllMedications } from '@mummssoftware/utils/constants';
import { notesIncluded } from '@mummssoftware/utils/utils';
import { PrintHeaderPDFComponent } from '../Header';
import { PrintFooterPDFComponent } from '../Footer';
import { ios, pageOrientation, pageSize, safari } from '../../utils';
import { styles } from '../../utils/styles';
import { MedNamePDF } from './components/MedNamePDF';
import { MedQuantityPDF } from './components/MedQuantityPDF';
import { MedPhysicianPDF } from './components/MedPhysicianPDF';
import { MedVerbalPDF } from './components/MedVerbalPDF';
import { MedCoveredPDF } from './components/MedCoveredPDF';
import { MedStartStopPDF } from './components/MedStartStopPDF';
import { MedNotesPDF } from './components/MedNotesPDF';
import defaults from '../../translations/en.json';
import { toSeriesList } from './utils';

const NoMedsDataComponent = () => /*#__PURE__*/React.createElement(View, {
  style: [styles.width100, styles.textAlignCenter, styles.marginYPointTwoCm]
}, /*#__PURE__*/React.createElement(Text, {
  style: styles.CPFTableDetailsText
}, defaults.noMedsAll));

const NoMedNotesDataComponent = () => /*#__PURE__*/React.createElement(View, {
  style: [styles.width100, styles.textAlignCenter]
}, /*#__PURE__*/React.createElement(Text, {
  style: styles.CPFTableDetailsText
}, defaults.noMedsNotes));

const medRowStyles = [styles.row, styles.width100, styles.pYPointOneCm, styles.alignItemsCenter];

const MedSeries = ({
  seriesMeds,
  seriesNotes,
  siteTimezone
}) => {
  const headRank = seriesMeds && seriesMeds[0].medseriesrank;
  return /*#__PURE__*/React.createElement(View, null, seriesMeds && seriesMeds.map(med => {
    const uniqueId = _uniqueId('medications-row-');

    return med && /*#__PURE__*/React.createElement(View, {
      key: uniqueId,
      style: seriesNotes ? [styles.borderBottomBlack, styles.paddingBottomPointOneCm] : []
    }, /*#__PURE__*/React.createElement(View, {
      style: [...medRowStyles, seriesNotes || styles.borderBottomBlack]
    }, /*#__PURE__*/React.createElement(MedNamePDF, {
      med: med,
      indent: med.medseriesrank !== headRank
    }), /*#__PURE__*/React.createElement(MedQuantityPDF, {
      med: med
    }), /*#__PURE__*/React.createElement(MedPhysicianPDF, {
      med: med
    }), /*#__PURE__*/React.createElement(MedVerbalPDF, {
      med: med
    }), /*#__PURE__*/React.createElement(MedCoveredPDF, {
      med: med
    }), /*#__PURE__*/React.createElement(MedStartStopPDF, {
      med: med,
      siteTimezone: siteTimezone
    })));
  }), seriesNotes && /*#__PURE__*/React.createElement(View, {
    style: [styles.width100, styles.borderBottomBlack]
  }, /*#__PURE__*/React.createElement(Text, {
    style: styles.medsTableRowHeaderText
  }, defaults.relatedNotes), /*#__PURE__*/React.createElement(MedNotesPDF, {
    notes: seriesNotes
  })));
};

var _ref = /*#__PURE__*/React.createElement(NoMedsDataComponent, null);

var _ref2 = /*#__PURE__*/React.createElement(NoMedNotesDataComponent, null);

const MedicationRows = ({
  pdfMedications,
  pdfMedicationsNotes,
  siteTimezone
}) => {
  const bySeries = toSeriesList(pdfMedications);
  return /*#__PURE__*/React.createElement(View, null, (!pdfMedications || pdfMedications.length < 0) && _ref, !pdfMedicationsNotes || Object.keys(pdfMedicationsNotes).length === 0 && _ref2, bySeries.map(series => {
    const seriesId = series[0].externalid;
    return /*#__PURE__*/React.createElement(MedSeries, {
      seriesMeds: series,
      seriesNotes: pdfMedicationsNotes && pdfMedicationsNotes[seriesId],
      siteTimezone: siteTimezone
    });
  }));
};

export const MedicationTable = ({
  pdfMedications,
  pdfMedicationsNotes,
  siteTimezone
}) => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(View, {
  style: [styles.row, styles.width100, styles.borderBottomBlack]
}, /*#__PURE__*/React.createElement(Text, {
  style: [styles.medsTableRowHeaderText, styles.width40]
}, defaults.medication), /*#__PURE__*/React.createElement(Text, {
  style: [styles.medsTableRowHeaderText, styles.width6]
}, defaults.qty), /*#__PURE__*/React.createElement(Text, {
  style: [styles.medsTableRowHeaderText, styles.width16]
}, defaults.physician), /*#__PURE__*/React.createElement(Text, {
  style: [styles.medsTableRowHeaderText, styles.width16]
}, defaults.verbalReceivedBy), /*#__PURE__*/React.createElement(Text, {
  style: [styles.medsTableRowHeaderText, styles.width6]
}, "$"), /*#__PURE__*/React.createElement(Text, {
  style: [styles.medsTableRowHeaderText, styles.width16]
}, defaults.startStop)), /*#__PURE__*/React.createElement(MedicationRows, {
  pdfMedications: pdfMedications,
  pdfMedicationsNotes: pdfMedicationsNotes,
  siteTimezone: siteTimezone
}));

const GroupedMeds = ({
  pdfMedications,
  pdfMedicationsNotes,
  siteTimezone
}) => {
  // utility map for sorting meds before display
  const activeHash = makeActiveIdHash(pdfMedications);
  const pending = pdfMedications.filter(med => med.isprescription);
  const medications = pdfMedications.filter(med => !med.isprescription);
  const activeSeriesMeds = getActiveSeriesMeds(medications, activeHash);
  const discontinuedMeds = getFullyDiscontinuedMeds(medications, activeHash);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    style: _objectSpread(_objectSpread({}, styles.printerHeaderText), {}, {
      marginBottom: '8px'
    })
  }, ACTIVE), /*#__PURE__*/React.createElement(MedicationTable, {
    pdfMedications: activeSeriesMeds,
    pdfMedicationsNotes: pdfMedicationsNotes,
    siteTimezone: siteTimezone
  }), /*#__PURE__*/React.createElement(Text, {
    style: _objectSpread(_objectSpread({}, styles.printerHeaderText), {}, {
      marginBottom: '8px',
      marginTop: '4px'
    })
  }, DISCONTINUED), /*#__PURE__*/React.createElement(MedicationTable, {
    pdfMedications: discontinuedMeds,
    pdfMedicationsNotes: pdfMedicationsNotes,
    siteTimezone: siteTimezone
  }), /*#__PURE__*/React.createElement(Text, {
    style: _objectSpread(_objectSpread({}, styles.printerHeaderText), {}, {
      marginBottom: '8px',
      marginTop: '4px'
    })
  }, PENDING), /*#__PURE__*/React.createElement(MedicationTable, {
    pdfMedications: pending,
    pdfMedicationsNotes: pdfMedicationsNotes,
    siteTimezone: siteTimezone
  }));
};

var _ref3 = /*#__PURE__*/React.createElement(PrintFooterPDFComponent, null);

export const MedicationsPDF = ({
  browser,
  pdfMedications,
  pdfMedicationsNotes,
  dashboardPrintConfig,
  patient,
  currentUser
}) => {
  const shallowDashboardPrintConfigView = dashboardPrintConfig && _objectSpread({}, dashboardPrintConfig);

  const MedicationsPrintConfig = shallowDashboardPrintConfigView === null || shallowDashboardPrintConfigView === void 0 ? void 0 : shallowDashboardPrintConfigView.ClearScripts;
  const MedicationsRadioChoice = MedicationsPrintConfig.view;
  const includeNotes = notesIncluded(MedicationsPrintConfig);
  return /*#__PURE__*/React.createElement(Page, {
    wrap: false,
    size: pageSize,
    orientation: pageOrientation,
    style: browser && browser.name === safari || browser && browser.name === ios ? styles.pageSafari : styles.page
  }, /*#__PURE__*/React.createElement(PrintHeaderPDFComponent, {
    browser: browser,
    patient: patient
  }), MedicationsRadioChoice === AllMedications ? /*#__PURE__*/React.createElement(GroupedMeds, {
    pdfMedications: pdfMedications,
    pdfMedicationsNotes: includeNotes ? pdfMedicationsNotes : undefined,
    siteTimezone: currentUser.siteTimezone
  }) : /*#__PURE__*/React.createElement(MedicationTable, {
    pdfMedications: pdfMedications,
    pdfMedicationsNotes: includeNotes ? pdfMedicationsNotes : undefined,
    siteTimezone: currentUser.siteTimezone
  }), /*#__PURE__*/React.createElement(View, {
    style: [styles.width100, styles.mYPointFiveCm]
  }, /*#__PURE__*/React.createElement(Text, {
    style: (styles.fontWeightBold, styles.fontSizeElevenPt)
  }, defaults.medsDisclosure)), _ref3);
};